import { patternRoute, simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { MeLayout } from '../components/me-layout/me-layout.js'
import { meNavForSection, profileSectionForPath } from '../components/me-layout/me-section.js'
import { Spinner } from '../components/spinner/spinner.js'
import { View } from '../types.js'
import { loadMyProfileIfNeeded, loadPortalRoleIfNeeded } from '../utils/load-utils.js'
import { BalanceTimeView } from '../views/playtime/balancetime/balancetime.js'
import { WorktimeView } from '../views/playtime/worktime/worktime.js'
import { EmployeeProfileRoute } from '../views/portal/employee-profile-route.js'
import { AssetsView } from '../views/portal/views/assets/assets-view.js'
import { DocumentsRoute } from '../views/portal/views/documents/documents-route.js'
import { TasksView } from '../views/portal/views/tasks/tasks-view.js'
import { balancetimeModel } from './balancetime.js'
import { worktimeModel } from './worktime.js'

// The "Me" (Mina) area — one URL namespace under /me, dispatched by section.
// EVERY section renders <MeLayout> at the top level so React preserves the
// banner + subnav instance across navigation: only the page content swaps (true
// client-side nav, no chrome flash). The profile sections additionally reuse one
// EmployeeProfileRoute instance, so switching among them never refetches.
const ensureMeData = (view: View) => {
    loadPortalRoleIfNeeded(view)
    loadMyProfileIfNeeded(view)
}

const renderProfile = (view: View, section: string | undefined) => {
    const roleState = loadPortalRoleIfNeeded(view)
    const myEmployeeId = roleState.data?.myEmployeeId ?? null
    return (
        <MeLayout view={view} active={meNavForSection(section)}>
            {!roleState.data ? (
                <Spinner />
            ) : myEmployeeId === null ? (
                // Users with no employee record can't use the rich profile.
                <div className="portal-profile__loading">
                    {t.portal.profile.notFound(view.state.lang)}
                </div>
            ) : (
                <EmployeeProfileRoute
                    view={view}
                    employeeId={myEmployeeId}
                    showSignature
                    meContext
                    section={profileSectionForPath(section)}
                />
            )}
        </MeLayout>
    )
}

class MeModel {
    public route = simpleRoute<View>('/me', {
        onEnter: ensureMeData,
        render: (view) => renderProfile(view, ''),
    })

    public sectionRoute = patternRoute<View, '/me/:section'>('/me/:section', {
        onEnter: (view, params) => {
            ensureMeData(view)
            if (params.section === 'balancetime') {
                balancetimeModel.onEnter(view)
            }
        },
        render: (view, params) => {
            const { section } = params

            if (section === 'tasks') {
                return (
                    <MeLayout view={view} active="tasks">
                        <TasksView lang={view.state.lang} />
                    </MeLayout>
                )
            }

            if (section === 'worktime') {
                return (
                    <MeLayout view={view} active="projects">
                        <WorktimeView {...worktimeModel.getProps(view)} />
                    </MeLayout>
                )
            }

            if (section === 'balancetime') {
                return (
                    <MeLayout view={view} active="worktime">
                        <BalanceTimeView {...balancetimeModel.getProps(view)} />
                    </MeLayout>
                )
            }

            if (section === 'assets') {
                return (
                    <MeLayout view={view} active="assets">
                        <AssetsView lang={view.state.lang} />
                    </MeLayout>
                )
            }

            if (section === 'documents') {
                const portalRole = loadPortalRoleIfNeeded(view).data
                return (
                    <MeLayout view={view} active="documents">
                        {portalRole ? (
                            <DocumentsRoute
                                view={view}
                                myEmployeeId={portalRole.myEmployeeId ?? null}
                                googleDriveUrl={portalRole.googleDriveUrl ?? null}
                            />
                        ) : (
                            <Spinner />
                        )}
                    </MeLayout>
                )
            }

            // data | time-away | settings → the self profile.
            return renderProfile(view, section)
        },
    })
}

export const meModel = new MeModel()
